import { useCallback } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import VendNumber, { vn } from 'vend-number'
import trim from 'lodash/trim'
import { trackError } from '@vend/utilities'
import {
  applyLspCashDiscountToOutstanding,
  applyLspCashDiscount,
  hasCashDiscountingServiceFee,
  roundCash,
} from '../../../../functions/payments'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectLspCashDiscountRate,
  selectOutstandingBalance,
  selectSaleFinals,
  selectSaleTotals,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { isCorrectlyRounded } from '../../../helpers/isCorrectlyRounded'
import {
  CashDiscountingPaymentOption,
  CashPaymentOption,
  IntegratedPaymentOption,
  LspayCashDiscountingPaymentOption,
  SalePayment,
} from '../PaymentModal/types'
import {
  RoundedNumber,
  CashPaymentInformation,
} from '../../../payments/cash-payment-modal/CashPaymentModal'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'
import { useHandleIntegrationPayment } from './useHandleIntegrationPayment'
import { useFetchPaymentTypes } from './useFetchPaymentTypes'

/** Normalise a `roundCash()` result into the {@link RoundedNumber} shape used by payment options. */
const toRoundedNumber = (
  value: ReturnType<typeof roundCash>
): RoundedNumber => ({
  rounded: vn(value.rounded.toString()),
  remainder: value.remainder,
})

/**
 * Build an {@link LspayCashDiscountingPaymentOption} from the un-discounted
 * rounded values, the payment type, and the LSPay rate.
 */
const buildLspayCashDiscountingOption = (
  paymentType: ApiRetailerPaymentType,
  rounding: string,
  amountTendered: VendNumber,
  balance: VendNumber | string | number,
  roundedAmountTendered: ReturnType<typeof roundCash>,
  roundedBalance: ReturnType<typeof roundCash>,
  exclusivePrice: number | string,
  referenceInclusivePrice: number | string,
  rate: number
): LspayCashDiscountingPaymentOption => ({
  paymentModalType: 'lspayCashDiscounting',
  paymentType,
  rounding,
  lspCashDiscountRate: rate,
  // Apply the LSPay discount to the single cash payment and the outstanding
  // balance the customer is settling in this transaction.
  roundedAmountTendered: toRoundedNumber(
    roundCash(
      applyLspCashDiscount(
        amountTendered.toString(),
        exclusivePrice,
        referenceInclusivePrice,
        rate
      ),
      paymentType
    )
  ),
  roundedBalance: toRoundedNumber(
    roundCash(
      applyLspCashDiscountToOutstanding(
        vn(balance).toString(),
        exclusivePrice,
        rate
      ),
      paymentType
    )
  ),
  originalRoundedAmountTendered: toRoundedNumber(roundedAmountTendered),
  originalRoundedBalance: toRoundedNumber(roundedBalance),
})

/**
 * Handle complete cash payment.
 *
 * @param onPaymentSuccess
 * @param roundingPaymentType Optional rounding payment type. Used when the
 *        cash payment leaves a remainder; the rounding type covers that
 *        remainder before the cash payment is saved via `onPaymentSuccess`.
 */
export const useCompleteCashPayment = (
  onPaymentSuccess: (result: SalePayment) => void,
  roundingPaymentType?: ApiRetailerPaymentType
) => {
  const saleManager = useSaleManager()

  const tenderRoundedCash = useCallback(
    (
      cashAmount: string,
      paymentType: ApiRetailerPaymentType,
      changeAmount?: string
    ) => {
      onPaymentSuccess({
        paymentType,
        paymentAmount: cashAmount,
        changeAmount,
      })
    },
    [onPaymentSuccess]
  )

  const completeRoundCashPayment = useCallback(
    (
      { remainder, cashAmount, changeAmount }: CashPaymentInformation,
      paymentType: ApiRetailerPaymentType
    ) => {
      if (vn(0).eq(remainder)) {
        tenderRoundedCash(cashAmount, paymentType, changeAmount)
      } else if (roundingPaymentType) {
        // Original code force types as not nullable - which is not safe
        return saleManager
          .pay(remainder, roundingPaymentType)
          .catch(err => {
            trackError(err)
          })
          .finally(() => {
            tenderRoundedCash(cashAmount, paymentType, changeAmount)
          })
      }
    },
    [roundingPaymentType, saleManager, tenderRoundedCash]
  )

  return {
    completeRoundCashPayment,
  }
}

/**
 * Handle cash payment
 * @returns function to handle cash payment, it should do all the necessary things to build cash payment option
 */
export const useHandleCashPayment = (
  amount: string,
  onPaymentSuccess: (result: SalePayment) => void
) => {
  const balance = useAppSelector(selectOutstandingBalance)
  const totals = useAppSelector(selectSaleTotals)
  const saleFinals = useAppSelector(selectSaleFinals)
  // The LSPay rate is fetched once on PaymentPanel mount by
  // `useApplyLspCashDiscount` and stored on the current sale. Reading from
  // Redux here keeps the cash-button click path synchronous (matching the
  // pre-LSPay behaviour) and avoids a redundant network call per click.
  const lspCashDiscountRate = useAppSelector(selectLspCashDiscountRate)
  const { handleIntegrationPayment } = useHandleIntegrationPayment(amount)
  const registerManager = useRegisterManager()
  const hasCashDiscounting = registerManager.hasCashDiscounting()

  const { systemPaymentTypes } = useFetchPaymentTypes()
  const { completeRoundCashPayment } = useCompleteCashPayment(
    onPaymentSuccess,
    systemPaymentTypes.roundingPaymentType
  )

  const handleCashDiscountingPayment = useCallback(
    (paymentType: ApiRetailerPaymentType): CashDiscountingPaymentOption => ({
      paymentModalType: 'cashDiscounting',
      paymentType,
      amountTendered: amount,
    }),
    [amount]
  )

  const handleCashPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ):
      | CashPaymentOption
      | CashDiscountingPaymentOption
      | LspayCashDiscountingPaymentOption
      | IntegratedPaymentOption
      | Promise<IntegratedPaymentOption>
      | undefined => {
      const amountTendered = vn(amount)
      const rounding = paymentType.config?.rounding || '0.01'
      const roundedAmountTendered = roundCash(amountTendered, paymentType)
      const roundedBalance = roundCash(balance, paymentType)

      if (paymentType.config && trim(paymentType.config.url)) {
        // Some people have cash, but with a URL for some reason. It behaves like an integrated payment for them
        return handleIntegrationPayment(paymentType)
      }

      if (hasCashDiscountingServiceFee(hasCashDiscounting, { totals })) {
        return handleCashDiscountingPayment(paymentType)
      }

      // LSPay cash discounting: route to the LSPay modal when the rate
      // populated by `useApplyLspCashDiscount` is greater than 0. We only
      // apply this for positive-balance, positive-tender flows — refunds
      // and zero-amount tenders fall through to the normal cash path.
      if (
        lspCashDiscountRate > 0 &&
        vn(balance).gt(0) &&
        amountTendered.gt(0)
      ) {
        return buildLspayCashDiscountingOption(
          paymentType,
          rounding,
          amountTendered,
          balance,
          roundedAmountTendered,
          roundedBalance,
          saleFinals.exclusivePrice,
          saleFinals.inclusivePrice,
          lspCashDiscountRate
        )
      }

      if (
        // Don't skip if it's not correctly rounded
        isCorrectlyRounded(amountTendered, vn(rounding)) &&
        // Skip if it's correctly rounded and 0 (0 dollar cash payment)
        (amountTendered.isZero() ||
          // Skip if it's correctly rounded, positive and greater than the balance
          (amountTendered.gt(0) && amountTendered.gt(balance)))
        // Don't ever skip for refunds as we always want to show the modal
      ) {
        completeRoundCashPayment(
          {
            remainder: roundedBalance.remainder.toString(),
            cashAmount: roundedAmountTendered.rounded.toString(),
          },
          paymentType
        )
        return undefined
      }

      return {
        paymentModalType: 'cash',
        paymentType,
        roundedAmountTendered: toRoundedNumber(roundedAmountTendered),
        roundedBalance: toRoundedNumber(roundedBalance),
        rounding,
      }
    },
    [
      amount,
      balance,
      completeRoundCashPayment,
      hasCashDiscounting,
      lspCashDiscountRate,
      handleIntegrationPayment,
      handleCashDiscountingPayment,
      saleFinals.exclusivePrice,
      saleFinals.inclusivePrice,
      totals,
    ]
  )

  return {
    handleCashPayment,
  }
}
